"use client";
import { useSalesContact } from "@/components/site/sales-contact-context";
import { useTranslations } from "next-intl";

import { siteWallClock } from "@/lib/datetime/site-timezone";
import type { SiteInfo } from "@/templates/site/types";
import { cn } from "@/lib/utils";

const TEXT_STRONG = "text-white/92";
const TEXT_BODY = "text-white/78";
const TEXT_DIM = "text-white/52";

type ContactRow = { label: string; value: string };

export function SiteFooter({ site, fullWidth = false }: { site: SiteInfo; fullWidth?: boolean }) {
  const t = useTranslations("site.footer");
  const tCommon = useTranslations("site.common");
  const sales = useSalesContact({ qrImage: site.footer?.qrImage, qrCaption: site.footer?.qrCaption });
  const data = { ...site.footer, about: site.footer?.about ?? "", qrImage: sales.qrImage, qrCaption: sales.qrCaption };

  const contacts: ContactRow[] = [
    { label: t("email"), value: data?.email ?? "" },
    { label: t("phone"), value: data?.phone ?? "" },
    { label: t("address"), value: data?.address ?? "" },
  ].filter((row) => row.value.trim());

  const hasAbout = Boolean(data?.about.trim());
  const hasQr = Boolean(data?.qrImage.trim());
  const hasMain = hasAbout || contacts.length > 0 || hasQr;

  const copyright =
    data.copyright?.trim() ||
    `© ${siteWallClock(new Date()).year} ${site.name}`;

  const legalLinks = data?.legalLinks ?? [];
  const beian = [data.icpHtml?.trim(), data.psbHtml?.trim()].filter(
    (html): html is string => Boolean(html),
  );

  return (
    <footer className="w-full bg-black px-5 py-2.5 text-white sm:px-10">
      <div className={cn("relative mx-auto w-full", !fullWidth && "max-w-[1440px]")}>
        {hasMain ? (
          <div className="mb-4 grid items-start gap-[clamp(24px,4vw,48px)] border-b border-white/12 pt-[30px] pb-5 justify-items-center text-center min-[900px]:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)_auto] min-[900px]:justify-items-stretch min-[900px]:text-left">
            {hasAbout ? (
              <section className="w-full max-w-[560px] min-[900px]:max-w-none">
                <h3
                  className={`mb-3.5 text-[clamp(16px,1.25vw,18px)] font-semibold leading-snug text-white`}
                >
                  {t("about", { name: site.shortName })}
                </h3>
                <p
                  className={`m-0 whitespace-pre-line text-left text-[clamp(13px,1.05vw,15px)] leading-[1.75] ${TEXT_BODY} min-[900px]:text-justify`}
                >
                  {data?.about}
                </p>
              </section>
            ) : null}

            {contacts.length > 0 ? (
              <div className="w-full max-w-[560px] min-[900px]:max-w-none">
                <h3 className="mb-3.5 text-[clamp(16px,1.25vw,18px)] font-semibold leading-snug text-white">
                  {t("contact")}
                </h3>
                <address className="m-0 text-left text-[clamp(13px,1.05vw,15px)] not-italic leading-[1.65]">
                  {contacts.map((row) => (
                    <p key={row.label} className="mb-2.5 last:mb-0">
                      <span className={TEXT_STRONG}>
                        {t("contactRow", { label: row.label, value: row.value })}
                      </span>
                    </p>
                  ))}
                </address>
              </div>
            ) : null}

            {hasQr ? (
              <div id="footer-contact" className="flex scroll-mt-20 flex-col items-center justify-start pt-2 text-center min-[900px]:pt-0">
                {/* 二维码来自后台上传，地址可能是 /uploads 或 OSS，不走 next/image */}
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  className="block size-[120px] rounded bg-white object-contain"
                  src={data?.qrImage}
                  alt={data?.qrCaption || t("qrAlt")}
                  width={120}
                  height={120}
                  loading="lazy"
                />
                {data?.qrCaption.trim() ? (
                  <p className={`mt-3 max-w-[260px] whitespace-pre-line break-words text-[13px] leading-snug text-white/72`}>
                    {data.qrCaption}
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="py-2 text-center">
          <p
            className={`m-0 text-[clamp(12px,0.95vw,13px)] leading-[1.65] ${TEXT_DIM}`}
          >
            {copyright}
          </p>
          {legalLinks.length > 0 || beian.length > 0 ? (
            <p
              className={`mt-1 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-[clamp(12px,0.95vw,13px)] leading-[1.65] ${TEXT_DIM} [&_a]:text-white/52 [&_a:hover]:text-white`}
            >
              {legalLinks.map((link, index) => (
                <span key={link.href} className="contents">
                  {index > 0 ? <span aria-hidden="true">·</span> : null}
                  <a href={link.href}>
                    {tCommon(link.key === "terms" ? "legalTerms" : "legalPrivacy")}
                  </a>
                </span>
              ))}
              {beian.map((html, index) => (
                <span key={html} className="contents">
                  {legalLinks.length > 0 || index > 0 ? (
                    <span aria-hidden="true">·</span>
                  ) : null}
                  {/* 备案号后台存的就是带 a 标签的 HTML，原样渲染 */}
                  <span dangerouslySetInnerHTML={{ __html: html }} />
                </span>
              ))}
            </p>
          ) : null}
        </div>
      </div>
    </footer>
  );
}
